import {
  ButtonCore,
  ButtonCoreBaseProps,
  ButtonCoreFullWidthProps,
  ButtonCoreIconOnlyProps,
  ButtonCoreWithSelectedProps,
} from './ButtonCore';
import { ButtonIconProps } from 'omakase/Button/buttonIcons';
import {
  OmakasePolymorphicComponent,
  OmakasePolymorphicComponentProps,
} from '../utils/OmakasePolymorphicComponent';
import { Theme, css } from '@emotion/react';
import Anchor from 'components/Anchor';
import React from 'react';

export interface FilterButtonIconEndCountProps {
  /**
   * A count at the end of the button
   */
  iconEndCount?: number | React.ReactNode;
}

export type FilterButtonProps = Omit<ButtonCoreBaseProps, 'size'> &
  ButtonCoreIconOnlyProps &
  FilterButtonIconEndCountProps &
  ButtonCoreFullWidthProps &
  Omit<ButtonCoreWithSelectedProps, 'variant'> &
  Pick<ButtonIconProps, 'iconStart'>;

const FilterButtonDefaultElement = 'button';
type FilterButtonTypeGuard = 'button' | typeof Anchor;

export const getEncircledSvg = (digit: string) => (
  <div
    css={({ colors }: Theme) => css`
      circle {
        fill: ${colors.fgDefault};
      }
    `}
  >
    <svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 130 130">
      <circle cx="50%" cy="50%" r="65"></circle>
      <text text-anchor="middle" fontSize={75} x="50%" y="50%" dy="0.35em" fill="white">
        {digit}
      </text>
    </svg>
  </div>
);

const FilterButton: OmakasePolymorphicComponent<
  FilterButtonProps,
  typeof FilterButtonDefaultElement,
  FilterButtonTypeGuard
> = React.forwardRef(
  <E extends React.ElementType = typeof FilterButtonDefaultElement>(
    {
      children,
      component,
      iconEndCount,
      ...rest
    }: OmakasePolymorphicComponentProps<FilterButtonProps, E, FilterButtonTypeGuard>,
    ref: React.Ref<HTMLButtonElement> | undefined
  ) => {
    const Component: React.ElementType = component || FilterButtonDefaultElement;

    return (
      <ButtonCore
        variant="basic"
        size="medium"
        ref={ref}
        component={Component}
        iconEnd={
          iconEndCount && typeof iconEndCount === 'number'
            ? {
                icon: getEncircledSvg(iconEndCount > 9 ? '9+' : `${iconEndCount}`),
                alignment: 'farRight',
              }
            : { icon: iconEndCount, alignment: 'farRight' }
        }
        {...rest}
      >
        {children}
      </ButtonCore>
    );
  }
);

export default FilterButton;
